import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  NonNullableFormBuilder,
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { AppButtonComponent } from '../../../shared/components/button/app-button.component';
import { UiService } from '../../../core/services/ui.service';
import { ProductService, Product } from '../../../core/services/product.service';
import { SnackbarService } from '../../../core/services/snackbar.service';

type CreateProductForm = {
  name: FormControl<string>;
  cost: FormControl<string>;
  stripeLink: FormControl<string>;
  stripePrice: FormControl<string>;
};

@Component({
  selector: 'app-product-management-create-product',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppButtonComponent],
  templateUrl: './product-management-create-product.component.html',
  styleUrl: './product-management-create-product.component.scss',
})
export class CreateProductComponent implements OnDestroy, OnChanges {
  @Input() product: Product | null = null;

  isVisible = false;
  isLoading = false;
  errorMessage = '';
  isEditMode = false;
  selectedProduct: Product | null = null;
  private subscription = new Subscription();

  createProductForm: FormGroup<CreateProductForm>;

  constructor(
    private uiService: UiService,
    private productManagementService: ProductService,
    private fb: NonNullableFormBuilder,
    private snackbarService: SnackbarService,
  ) {
    const modalSub = this.uiService.createProductModal$.subscribe((visible) => {
      this.isVisible = visible;
    });

    this.subscription.add(modalSub);

    this.createProductForm = this.fb.group<CreateProductForm>({
      name: this.fb.control('', {
        validators: [Validators.required],
      }),
      cost: this.fb.control('', {
        validators: [Validators.required],
      }),
      stripeLink: this.fb.control('', {
        validators: [Validators.required],
      }),
      stripePrice: this.fb.control('', {
        validators: [Validators.required],
      }),
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['product'] && this.product) {
      this.isEditMode = true;
      this.createProductForm.patchValue({
        name: this.product.name,
        cost: this.product.costInCredits,
        stripeLink: this.product.stripeLink,
        stripePrice: this.product.stripePrice,
      });
    } else if (!this.product) {
      this.isEditMode = false;
      this.createProductForm.reset();
    }
  }

  closeModal(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.uiService.closeCreateProductModal();
    this.isEditMode = false;
    this.errorMessage = '';
    this.createProductForm.reset();
  }

  onSubmit(): void {
    if (this.createProductForm.invalid) return;
    this.isLoading = false;
    this.errorMessage = '';

    const { name, cost, stripeLink, stripePrice } = this.createProductForm.getRawValue();
    const cost_in_credits = cost;
    const stripe_link = stripeLink;
    const stripe_price = stripePrice;

    if (this.product?.paymentMatchId) {
      this.productManagementService
        .updateProduct(this.product.paymentMatchId, name, cost_in_credits, stripe_link, stripe_price)
        .subscribe({
          next: (updatedProduct) => {
            this.isLoading = false;
            this.snackbarService.show('Product updated successfully.', 'success');
            this.uiService.notifyProductUpdated(updatedProduct);
            this.closeModal();
            this.createProductForm.reset();
          },
          error: (err) => {
            this.isLoading = false;
            this.errorMessage = err.error?.error || 'Error updating product';
          },
        });
    } else {
      this.productManagementService.createProduct(name, cost, stripeLink, stripePrice).subscribe({
        next: (newProduct) => {
          this.isLoading = false;
          this.snackbarService.show('New product successfully added.', 'success');
          this.createProductForm.reset();
          this.uiService.notifyProductAdded(newProduct);
          this.closeModal();
        },
        error: (err) => {
          this.isLoading = false;
          this.errorMessage = err.error?.error || 'Error creating product';
        },
      });
    }
  }

  get isFormInvalid(): boolean {
    return this.createProductForm.invalid;
  }

  onUpdateProduct(product: Product) {
    this.selectedProduct = product;
    this.uiService.openCreateProductModal();
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
